import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QuantityControl } from './QuantityControl';
import { ProductImageGalleryModal } from './ProductImageGalleryModal';
import { useCartStore } from '@/stores/cartStore';
import { useAddToCartModal } from '@/contexts/AddToCartModalContext';
import { formatPrice } from '@/utils/shopFormatters';
import { toast } from 'sonner';
import type { ProductVariant, SubVariant, SelectedVariantPath } from '@/types/cart';
import { trackEvent } from '@/utils/analytics';
import { userUtils } from '@/utils/auth';

export function AddToCartModal() {
  const navigate = useNavigate();
  const { isOpen, product, triggerSource, existingCartItem, closeModal } = useAddToCartModal();
  const { addToCart, updateCartItem } = useCartStore();
  const user = userUtils.getUser();
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [selectedSubVariantId, setSelectedSubVariantId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  // Get the currently selected variant and sub-variant objects
  const selectedVariant = useMemo(() => {
    if (!product?.variants || !selectedVariantId) return null;
    return product.variants.find(v => v._id === selectedVariantId) || null;
  }, [product, selectedVariantId]);

  const selectedSubVariant = useMemo(() => {
    if (!selectedVariant?.subVariants || !selectedSubVariantId) return null;
    return selectedVariant.subVariants.find(sv => sv._id === selectedSubVariantId) || null;
  }, [selectedVariant, selectedSubVariantId]);

  // Calculate the effective price based on selections
  const effectivePrice = useMemo(() => {
    if (!product) return 0;
    
    // If sub-variant is selected, use its price
    if (selectedSubVariant) {
      return selectedSubVariant.price;
    }
    
    // If only main variant is selected, use its price
    if (selectedVariant) {
      return selectedVariant.price;
    }
    
    // Fall back to base product price
    return product.discount?.isActive ? product.discount.discountedPrice : product.price;
  }, [product, selectedVariant, selectedSubVariant]);

  // Get available stock based on selection
  const availableStock = useMemo(() => {
    if (!product) return 0;
    
    // If sub-variant is selected, use its stock
    if (selectedSubVariant) {
      return selectedSubVariant.stock;
    }
    
    // If only main variant is selected, use its total stock
    if (selectedVariant) {
      return selectedVariant.totalStock;
    }
    
    // Fall back to product stock
    return product.stock;
  }, [product, selectedVariant, selectedSubVariant]);

  // Initialize state when modal opens
  useEffect(() => {
    if (isOpen && product) {
      if (existingCartItem?.selectedVariantPath) {
        setSelectedVariantId(existingCartItem.selectedVariantPath.variantId);
        setSelectedSubVariantId(existingCartItem.selectedVariantPath.subVariantId || null);
        setQuantity(existingCartItem.quantity);
      } else {
        setSelectedVariantId(null);
        setSelectedSubVariantId(null);
        setQuantity(1);
      }
    }
  }, [isOpen, product, existingCartItem]);

  // Check if selection is valid
  const isValid = useMemo(() => {
    if (!product) return false;
    
    // If product has no variants, it's always valid
    if (!product.variants || product.variants.length === 0) {
      return true;
    }
    
    // Must select a main variant
    if (!selectedVariantId) return false;
    
    // If the selected variant has sub-variants, must select one
    if (selectedVariant?.subVariants && selectedVariant.subVariants.length > 0) {
      return selectedSubVariantId !== null;
    }
    
    return true;
  }, [product, selectedVariantId, selectedVariant, selectedSubVariantId]);

  const handleVariantSelect = (variant: ProductVariant) => {
    setSelectedVariantId(variant._id);
    // Reset sub-variant when main variant changes
    setSelectedSubVariantId(null);
  };

  const handleSubVariantSelect = (subVariant: SubVariant) => {
    setSelectedSubVariantId(subVariant._id);
  };

  const handleIncrement = () => {
    setQuantity((prev) => Math.min(prev + 1, Math.min(availableStock, 99)));
  };

  const handleDecrement = () => {
    setQuantity((prev) => Math.max(prev - 1, 1));
  };

  const handleAddToCart = () => {
    if (!product || !isValid) {
      toast.error('Please select all required options');
      return;
    }

    try {
      // Build the selected variant path
      const selectedVariantPath: SelectedVariantPath | undefined = selectedVariantId ? {
        variantId: selectedVariantId,
        variantName: selectedVariant!.name,
        variantValue: selectedVariant!.value,
        ...(selectedSubVariantId && selectedSubVariant ? {
          subVariantId: selectedSubVariantId,
          subVariantName: selectedSubVariant.name,
          subVariantValue: selectedSubVariant.value,
        } : {})
      } : undefined;

      // Get the appropriate product image
      const productImage = selectedVariant?.images?.[0]?.url 
        || selectedVariant?.images?.[0]?.data
        || product.images?.[0]?.url 
        || '';

      // Get discount info
      const discount = selectedVariant?.discount || product.discount;
      const discountPercentage = discount?.isActive ? discount.value : 0;
      const originalPrice = selectedSubVariant?.price || selectedVariant?.price || product.price;

      if (existingCartItem && triggerSource === 'cart') {
        // Update existing cart item
        updateCartItem(existingCartItem.id, {
          selectedVariantPath,
          quantity,
          price: effectivePrice,
          originalPrice,
          discountPercentage,
          discount,
          productImage,
        });
      } else {
        // Add new item to cart
        addToCart({
          productId: product._id,
          productName: product.name,
          productImage,
          price: effectivePrice,
          originalPrice,
          discountPercentage,
          discount,
          selectedVariantPath,
          quantity,
          sellerId: product.seller._id,
          sellerName: product.seller.storeInfo.storeName,
          weight: product.shippingInfo.weight.value,
        });
      }

      if (user) {
        trackEvent('add_to_cart', {
          userId: user?._id,
          email: user?.email,
          name: user?.firstName + ' ' + user?.lastName,
          username: user?.username,
          country: user?.country,
          isActive: user?.isActive,
          isEmailVerified: user?.emailVerified,
          isPhoneVerified: user?.phoneVerified,
          isDisabled: user?.isDisabled,
          isNewBuyer: user?.isNewBuyer,
          isIdentityVerified: user?.isIdentityVerified,
          productId: product?._id,
          productName: product?.name,
          productImage: productImage,
          price: effectivePrice,
          originalPrice: originalPrice,
          discountPercentage: discountPercentage,
          quantity: quantity,
          sellerId: product?.seller?._id,
          sellerName: product?.seller?.storeInfo?.storeName,
          weight: product?.shippingInfo?.weight?.value,
        });

        // Track Meta Pixel event only if fbq is available (production environment)
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          window.fbq('track', 'AddToCart', {
            content_ids: product?._id,
            content_name: product?.name,
            content_type: 'product',
            value: effectivePrice,
            currency: 'NGN',
          });
        }
      }

      closeModal();
      
      // If triggered from "Buy Now" (DETAIL_PAGE), store sellerId and navigate to checkout
      if (triggerSource === 'blip_feed') {
        // Store seller ID in sessionStorage for checkout
        sessionStorage.setItem('checkoutSellerId', product.seller._id);
        
        // Navigate to checkout after a short delay
        setTimeout(() => {
          navigate('/checkout');
        }, 500);
      } else {
        // For regular "Add to Cart", just show success toast
        toast.success('Added to cart');
      }
    } catch (error) {
      toast.error('Failed to add item to cart');
      console.error('Add to cart error:', error);
    }
  };

  if (!product) return null;

  const totalPrice = effectivePrice * quantity;
  const isEditMode = existingCartItem && triggerSource === 'cart';

  // Get base discount for display
  const baseDiscount = product.discount?.isActive ? product.discount.value : 0;
  const baseOriginalPrice = product.pricing?.originalPrice || product.price;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent 
        className="max-w-[476px] w-[95vw] sm:w-full p-0 gap-0 rounded-[5px] border border-black/5 shadow-[0px_4px_4px_rgba(0,0,0,0.25)] max-h-[90vh] overflow-y-auto"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-black/5">
          <DialogTitle className="font-lexend text-[1.1rem] sm:text-[1.25rem] font-medium tracking-[-0.22px] capitalize text-black/80 text-center">
            {isEditMode ? 'Update Cart Item' : 'Go To Checkout'}
          </DialogTitle>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-2 sm:gap-3 p-4 sm:p-6">
          {/* Product Image Gallery */}
          <ProductImageGalleryModal images={product.images || []} productName={product.name} />

          {/* Product Title */}
          <h2 className="font-lexend text-[0.9rem] sm:text-[1rem] font-medium tracking-[-0.16px] leading-[1.4] capitalize text-black line-clamp-2">
            {product.name}
          </h2>

          {/* Price Section */}
          <div className="flex flex-col gap-1 sm:gap-2">
            <span className="font-lexend text-[1.5rem] sm:text-[1.875rem] font-bold tracking-[-0.22px] leading-[1] capitalize text-black">
              {formatPrice(effectivePrice)}
            </span>
            {baseDiscount > 0 && (
              <div className="flex items-center gap-2">
                <span className="font-lexend text-[0.7rem] sm:text-[0.75rem] font-normal tracking-[-0.12px] capitalize text-[#F12626]">
                  {baseDiscount}% off
                </span>
                <span className="font-lexend text-[0.8rem] sm:text-[0.875rem] font-light tracking-[-0.22px] capitalize text-black/50 line-through">
                  {formatPrice(baseOriginalPrice)}
                </span>
              </div>
            )}
          </div>

          {/* Variant Selectors */}
          {product.variants && product.variants.length > 0 && (
            <div className="flex flex-col gap-4 sm:gap-5 mt-2">
              {/* Group variants by name (e.g., all "Color" variants together) */}
              {Object.entries(
                product.variants.reduce((acc, variant) => {
                  if (!acc[variant.name]) acc[variant.name] = [];
                  acc[variant.name].push(variant);
                  return acc;
                }, {} as Record<string, ProductVariant[]>)
              ).map(([variantName, variants]) => (
                <div key={variantName} className="flex flex-col gap-2">
                  <span className="font-lexend text-[0.9rem] sm:text-[1rem] font-medium tracking-[-0.16px] capitalize text-black">
                    {variantName}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {variants.map((variant) => {
                      const isSelected = selectedVariantId === variant._id;
                      const isDisabled = variant.totalStock === 0;

                      return (
                        <button
                          key={variant._id}
                          type="button"
                          onClick={() => !isDisabled && handleVariantSelect(variant)}
                          disabled={isDisabled}
                          className={`
                            relative h-auto px-3 sm:px-4 py-2 rounded-[20px] border 
                            font-lexend text-[0.875rem] sm:text-[1rem] font-normal 
                            tracking-[-0.16px] capitalize transition-all duration-200 min-w-[60px]
                            flex items-center gap-2
                            ${isSelected
                              ? 'bg-primary-green text-white border-primary-green hover:bg-primary-green/90'
                              : 'bg-white text-black border-black/20 hover:bg-gray-50'}
                            ${isDisabled && 'opacity-50 cursor-not-allowed'}
                            ${!isDisabled && 'cursor-pointer'}
                          `}
                        >
                          {/* Variant image thumbnail if available */}
                          {variant.images && variant.images.length > 0 && (
                            <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                              <img 
                                src={variant.images[0].url || variant.images[0].data} 
                                alt={variant.value}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          
                          <span className="flex-1">{variant.value}</span>

                          {/* Out of stock indicator */}
                          {isDisabled && (
                            <span className="absolute inset-0 flex items-center justify-center bg-white/80 text-[0.7rem] text-red-600 font-medium rounded-[20px]">
                              Out of Stock
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Sub-Variants (if main variant is selected and has sub-variants) */}
              {selectedVariant && selectedVariant.subVariants && selectedVariant.subVariants.length > 0 && (
                <div className="flex flex-col gap-2 pl-2 border-l-2 border-gray-200">
                  <span className="font-lexend text-[0.85rem] sm:text-[0.9rem] font-medium tracking-[-0.16px] capitalize text-black/80">
                    {selectedVariant.subVariants[0].name}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {selectedVariant.subVariants.map((subVariant) => {
                      const isSelected = selectedSubVariantId === subVariant._id;
                      const isDisabled = subVariant.stock === 0;

                      return (
                        <button
                          key={subVariant._id}
                          type="button"
                          onClick={() => !isDisabled && handleSubVariantSelect(subVariant)}
                          disabled={isDisabled}
                          className={`
                            relative h-auto px-3 sm:px-4 py-2 rounded-[20px] border 
                            font-lexend text-[0.875rem] sm:text-[1rem] font-normal 
                            tracking-[-0.16px] capitalize transition-all duration-200 min-w-[60px]
                            ${isSelected
                              ? 'bg-primary-green text-white border-primary-green hover:bg-primary-green/90'
                              : 'bg-white text-black border-black/20 hover:bg-gray-50'}
                            ${isDisabled && 'opacity-50 cursor-not-allowed'}
                            ${!isDisabled && 'cursor-pointer'}
                          `}
                        >
                          {subVariant.value}
                          
                          {/* Show price difference if applicable */}
                          {subVariant.price !== selectedVariant.price && (
                            <span className={`text-[0.75rem] font-medium ml-1 ${
                              isSelected ? 'text-white' : 'text-primary-green'
                            }`}>
                              ₦{subVariant.price.toLocaleString()}
                            </span>
                          )}

                          {/* Out of stock indicator */}
                          {isDisabled && (
                            <span className="absolute inset-0 flex items-center justify-center bg-white/80 text-[0.7rem] text-red-600 font-medium rounded-[20px]">
                              Out of Stock
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stock indicator */}
          {availableStock < 10 && availableStock > 0 && (
            <p className="text-[0.75rem] text-orange-600 font-medium">
              Only {availableStock} left in stock
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 px-4 sm:px-6 py-4 sm:py-6 border-t border-black/10">
          <QuantityControl
            quantity={quantity}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
          />

          <Button
            onClick={handleAddToCart}
            disabled={!isValid || availableStock === 0}
            className="flex-1 h-[48px] sm:h-[51px] bg-primary-green hover:bg-primary-green/90 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-[10px] font-lexend text-[0.875rem] sm:text-[0.9375rem] font-medium tracking-[-0.22px] capitalize text-[#F4FFF8]"
          >
            {isEditMode ? 'Update Cart' : `Add ${formatPrice(totalPrice)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}